<template>
  <div>
    <div class="mb-6">
      <label class="label cursor-pointer">
        <span class="label-text text-base font-medium">{{ t('aiChatAgentSetup.avatars.did.presenterIdLabel', 'Presenter ID') }}</span>
      </label>
      <div class="join w-full">
        <input
          type="text"
          class="input input-bordered join-item flex-1"
          :value="localPresenterId"
          @input="updatePresenterId($event.target.value)"
          :placeholder="t('aiChatAgentSetup.avatars.did.presenterIdPlaceholder', 'Enter presenter id')"
        />
        <a
          class="btn btn-secondary join-item"
          href="https://docs.d-id.com/reference/getpresenters"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ t('aiChatAgentSetup.avatars.did.viewCatalogButton', 'View Catalog') }}
        </a>
      </div>
      <div class="mt-2 text-sm text-gray-500 flex items-center gap-2">
        <span class="icon-[heroicons--information-circle] w-5 h-5"></span>
        <span>{{ t('aiChatAgentSetup.avatars.did.infoAlert', 'Copy the presenter id from the D-ID catalog and paste it here.') }}</span>
      </div>
    </div>
    <div class="mb-6">
      <label class="label cursor-pointer">{{ t('aiChatAgentSetup.avatars.did.previewLabel', 'Current Preview') }}</label>
      <div class="border border-base-300 rounded-lg p-4 flex flex-col md:flex-row md:items-center">
        <div class="md:w-1/2 mr-0 md:mr-4 mb-4 md:mb-0">
          <div v-if="previewUrl !== ''" class="relative rounded-md overflow-hidden" style="padding-top: 56.25%;">
            <video
              :src="previewUrl"
              autoplay
              muted
              loop
              playsinline
              class="absolute inset-0 w-full h-full object-cover"
            ></video>
          </div>
          <div v-else class="text-sm text-gray-500 text-center border border-dashed rounded-md p-6">
            {{ t('aiChatAgentSetup.avatars.did.noPreview', 'No preview available yet') }}
          </div>
        </div>
        <div class="md:w-1/2">
          <div class="font-semibold">{{ currentName }}</div>
          <div class="text-xs text-gray-500 break-all mt-1">{{ presenterId }}</div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, watch } from 'vue';
import { useI18n } from '@/i18n';
import { didPresenters } from './didPresenters';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true
  }
});
const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const defaultState = {
  presenterId: '',
  presenterName: '',
  previewUrl: ''
};
const normalizedPresenters = didPresenters.map(item => {
  const encodedId = encodeURIComponent(item.id);
  const previewUrl = `https://d-id-public-bucket.s3.us-west-2.amazonaws.com/presenters/${encodedId}.mp4`;
  return {
    ...item,
    previewUrl
  };
});
const safeValue = computed(() => ({
  ...defaultState,
  ...props.modelValue
}));
const presenterId = computed(() => safeValue.value.presenterId);
const previewUrl = computed(() => safeValue.value.previewUrl);
const currentName = computed(() => {
  if (safeValue.value.presenterName !== '') {
    return safeValue.value.presenterName;
  }
  return t('aiChatAgentSetup.avatars.did.noSelection', 'No presenter selected');
});
const localPresenterId = computed(() => presenterId.value);
function buildPreviewUrl(id) {
  if (typeof id !== 'string' || id.trim().length === 0) {
    return '';
  }
  const encodedId = encodeURIComponent(id);
  return `https://d-id-public-bucket.s3.us-west-2.amazonaws.com/presenters/${encodedId}.mp4`;
}
function updateValue(patch) {
  const nextValue = {
    ...safeValue.value,
    ...patch
  };
  emit('update:modelValue', nextValue);
}
function updatePresenterId(value) {
  updateValue({
    presenterId: value
  });
}
watch(
  () => presenterId.value,
  value => {
    const trimmed = typeof value === 'string' ? value.trim() : '';
    if (trimmed.length === 0) {
      if (safeValue.value.presenterName !== '' || safeValue.value.previewUrl !== '') {
        updateValue({
          presenterName: '',
          previewUrl: ''
        });
      }
      return;
    }
    const presetMatch = normalizedPresenters.find(item => item.id === trimmed);
    if (presetMatch !== undefined) {
      const patch = {};
      if (safeValue.value.presenterName !== presetMatch.name) {
        patch.presenterName = presetMatch.name;
      }
      if (safeValue.value.previewUrl !== presetMatch.previewUrl) {
        patch.previewUrl = presetMatch.previewUrl;
      }
      if (Object.keys(patch).length > 0) {
        updateValue(patch);
      }
      return;
    }
    const fallbackUrl = buildPreviewUrl(trimmed);
    const needsUpdate = safeValue.value.previewUrl !== fallbackUrl || safeValue.value.presenterName !== '';
    if (needsUpdate === true) {
      updateValue({
        presenterName: '',
        previewUrl: fallbackUrl
      });
    }
  }
);
</script>

